import { useEffect, useState } from "react";
import api from "../lib/api";
import { CalendarDays } from "lucide-react";

const todayIST = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
const fmtDay = (d) => new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

const COLS = [
  ["fleet", "TD cars", "Test-drive vehicles in the operational fleet"],
  ["available", "Available now", "Cars free for a test drive right now"],
  ["td_mtd", "TDs MTD", "Test drives given this month to date"],
  ["td_per_car_day_mtd", "TD / car / day (MTD)", "TDs MTD ÷ cars ÷ days elapsed"],
  ["td_day", "TDs on day", "Test drives given on the selected day"],
  ["td_per_car_day", "TD / car on day", "TDs on the day ÷ cars"],
];

function Table({ title, rows, totals, testId }) {
  return (
    <div data-testid={testId} className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-4 py-3 font-heading text-sm font-bold text-[#0E2240]">{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-2.5">{title.includes("model") ? "Model" : "Branch"}</th>
              {COLS.map(([k, l, tip]) => <th key={k} title={tip} className="px-3 py-2.5 text-right whitespace-nowrap">{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} data-testid={`${testId}-row-${r.key}`} className="border-t border-slate-100 hover:bg-slate-50/60">
                <td className="px-4 py-2.5 font-semibold text-[#0E2240] whitespace-nowrap">{r.label}</td>
                {COLS.map(([k]) => (
                  <td key={k} className={`px-3 py-2.5 text-right tabular-nums ${k === "td_day" && r[k] > 0 ? "font-bold text-[#BB162B]" : k === "td_mtd" ? "font-bold text-[#0E2240]" : "text-slate-700"}`}>{r[k]}</td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={COLS.length + 1} className="px-4 py-6 text-center text-slate-400">No data</td></tr>}
          </tbody>
          {totals && rows.length > 0 && (
            <tfoot className="border-t-2 border-slate-200 bg-slate-50 font-bold text-[#0E2240]">
              <tr>
                <td className="px-4 py-2.5">{totals.label}</td>
                {COLS.map(([k]) => <td key={k} className="px-3 py-2.5 text-right tabular-nums">{totals[k]}</td>)}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

export default function TDSummary() {
  const [date, setDate] = useState(todayIST());
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/analytics/td-summary", { params: { date } }).then((r) => setD(r.data)).catch(() => {}); }, [date]);
  const t = d?.totals;

  return (
    <div className="px-4 py-5 lg:px-8 max-w-6xl">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#0E2240]">Test Drive Summary</h1>
          <p className="mt-0.5 text-xs text-slate-500">Branch-wise and model-wise productivity · {d ? `${d.month} · ${d.days_elapsed} day${d.days_elapsed > 1 ? "s" : ""} elapsed` : "…"}</p>
        </div>
        <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-[#0E2240]">
          <CalendarDays size={14} className="text-slate-400" /> Day
          <input data-testid="td-summary-date" type="date" value={date} max={todayIST()} onChange={(e) => e.target.value && setDate(e.target.value)} className="bg-transparent text-xs focus:outline-none" />
        </label>
      </div>

      {t && (
        <div data-testid="td-summary-strip" className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          {COLS.map(([k, l]) => (
            <div key={k} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
              <div className={`font-heading text-xl font-bold ${k === "td_day" ? "text-[#BB162B]" : "text-[#0E2240]"}`}>{t[k]}</div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{l}</div>
            </div>
          ))}
        </div>
      )}

      {!d ? <div className="mt-8 text-slate-400">Loading…</div> : (
        <>
          <Table title={`Branch-wise · ${fmtDay(d.date)}`} rows={d.branches} totals={d.totals} testId="td-summary-branches" />
          <Table title={`By car model · ${fmtDay(d.date)}`} rows={d.models} totals={{ ...d.totals, label: "All models" }} testId="td-summary-models" />
          <p className="mt-3 text-[10px] text-slate-400">TDs count closed drives (connected-drive segments and approved extra drives included; Admin force-closes without gate scans excluded). Per-car rates use the current operational fleet. Days are in IST.</p>
        </>
      )}
    </div>
  );
}
